import { SystemStatusBadge } from '@tempo-io/sui-badge';
import { Button } from '@tempo-io/sui-button';
import { KeyboardArrowDown, Sync } from '@tempo-io/sui-icons';
import { Skeleton } from '@tempo-io/sui-skeleton';
import { toast } from '@tempo-io/sui-sonner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ItemSyncStatusView,
  SynchronizationStatusValues,
  WorkIntegration_Keys,
} from '@/common/api/types/workIntegrations';
import { useRequestItemSyncMutation } from '@/features/settings/work-integrations/mutations/useRequestItemSyncMutation';
import { useGetAllWorkIntegrations } from '@/features/settings/work-integrations/queries/useGetAllWorkIntegrations';
import { useGetItemSyncFailedItems } from '@/features/settings/work-integrations/queries/useGetItemSyncFailedItems';
import { useGetWorkIntegrationIdsByItemIds } from '@/features/settings/work-integrations/queries/useGetWorkIntegrationItems';
import { useItemSyncStatusesQuery } from '@/features/settings/work-integrations/queries/useItemSyncStatusesQuery';
import { useIsPublishedContext } from '@/features/views/components/published/PublishedContext';
import { AssignIntegrationDialog } from './AssignIntegrationDialog';

interface IntegrationFieldProps {
  itemId: number;
  loopId: number;
  itemTypeId: number;
  onViewSyncFailures?: () => void;
}

const SelectIntegrationButton = ({
  disabled,
  name,
  isAssigned,
  onClick,
}: {
  disabled: boolean;
  name: string;
  isAssigned: boolean;
  onClick: () => void;
}) => (
  <Button
    data-testid="item-panel-integration-add-button"
    variant="outline"
    onClick={onClick}
    disabled={disabled}
    className="h-10 w-full min-w-0 justify-between rounded-md border bg-bg-surface-base px-3 py-2 text-left font-normal shadow-sm disabled:hover:border-border-neutral-subtle disabled:hover:bg-bg-surface-base disabled:hover:shadow-sm"
  >
    <span className={`truncate ${isAssigned ? 'text-text-neutral-secondary' : ''}`}>{name}</span>
    <KeyboardArrowDown className="shrink-0 text-icon-neutral-secondary opacity-80" />
  </Button>
);

const FailedStatusBadge = () => {
  const { t } = useTranslation();
  return (
    <SystemStatusBadge variant="error" data-testid="item-panel-integration-field-failed-badge">
      {t(($) => $.adaptivePlanner.itemPanel.integration.failedBadge)}
    </SystemStatusBadge>
  );
};

export const IntegrationField = ({
  itemId,
  loopId,
  itemTypeId,
  onViewSyncFailures,
}: IntegrationFieldProps) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const itemIds = useMemo(() => [itemId], [itemId]);

  const { data: integrationIdsByItemId, isLoading: isAssociatedLoading } =
    useGetWorkIntegrationIdsByItemIds(itemIds);
  const { data: syncStatusesByItemId, isLoading: isSyncStatusLoading } =
    useItemSyncStatusesQuery(itemIds);
  const { data: integrations } = useGetAllWorkIntegrations({}, { filter: { loopIds: [loopId] } });
  const requestSync = useRequestItemSyncMutation();

  const integrationsById = useMemo(
    () => new Map((integrations ?? []).map((integration) => [integration.id, integration])),
    [integrations],
  );

  const eligibleIntegrationsCount = useMemo(
    () =>
      (integrations ?? []).filter(
        (integration) =>
          integration.sourceSystemType === WorkIntegration_Keys.JIRA &&
          integration.synchronizationStatus === SynchronizationStatusValues.ENABLED,
      ).length,
    [integrations],
  );

  const associatedIntegrationId =
    integrationIdsByItemId?.[itemId] ?? syncStatusesByItemId?.[itemId]?.integrationId ?? undefined;
  const isAssigned = associatedIntegrationId !== undefined;

  const associatedIntegration = isAssigned
    ? integrationsById.get(associatedIntegrationId)
    : undefined;

  const associatedIntegrationName =
    associatedIntegration?.name ??
    (isAssigned
      ? t(($) => $.adaptivePlanner.itemPanel.integration.unnamedFallback, {
          id: associatedIntegrationId,
        })
      : undefined);

  const syncState = syncStatusesByItemId?.[itemId]?.state;
  const syncErrorMessage = syncStatusesByItemId?.[itemId]?.errorMessage;

  const isSyncing =
    syncState === ItemSyncStatusView.IN_PROGRESS ||
    requestSync.isPending ||
    (requestSync.isSuccess && syncState === ItemSyncStatusView.PENDING);

  const isSynced = syncState === ItemSyncStatusView.SYNCED;
  const isFailed = syncState === ItemSyncStatusView.FAILED;

  const isPublishedContext = useIsPublishedContext();
  const { data: itemSyncFailures } = useGetItemSyncFailedItems({
    itemIdOrSourceItemKey: isFailed && !isPublishedContext ? String(itemId) : undefined,
    page: 0,
    pageSize: 20,
  });
  const hasSyncFailureRecords = (itemSyncFailures?.totalCount ?? 0) > 0;
  // ItemDetails mounts the Sync Failures tab from this same failed-items query, so whenever this is
  // true the tab is guaranteed present for onViewSyncFailures to switch to.
  const canViewSyncFailures = hasSyncFailureRecords && onViewSyncFailures !== undefined;
  const syncStateTestId =
    syncState === ItemSyncStatusView.SYNCED
      ? 'item-panel-integration-field-synced'
      : syncState === ItemSyncStatusView.IN_PROGRESS
        ? 'item-panel-integration-field-in-progress'
        : syncState === ItemSyncStatusView.FAILED
          ? 'item-panel-integration-field-failed'
          : syncState === ItemSyncStatusView.PENDING
            ? 'item-panel-integration-field-pending'
            : undefined;

  const syncWasRequestedRef = useRef(false);
  const syncSeenInflightRef = useRef(false);

  useEffect(() => {
    if (requestSync.isPending) {
      syncWasRequestedRef.current = true;
    }
  }, [requestSync.isPending]);

  useEffect(() => {
    if (
      syncWasRequestedRef.current &&
      (syncState === ItemSyncStatusView.PENDING || syncState === ItemSyncStatusView.IN_PROGRESS)
    ) {
      syncSeenInflightRef.current = true;
    }
  }, [syncState]);

  useEffect(() => {
    if (
      syncWasRequestedRef.current &&
      syncSeenInflightRef.current &&
      !isSyncing &&
      syncState === ItemSyncStatusView.FAILED
    ) {
      toast.error(t(($) => $.adaptivePlanner.itemPanel.integration.syncFailedToast));
      syncWasRequestedRef.current = false;
      syncSeenInflightRef.current = false;
    }
  }, [isSyncing, syncState, t]);

  const handleSyncClick = () => {
    requestSync.mutate(
      { itemId, integrationId: associatedIntegrationId },
      {
        onError: () =>
          toast.error(t(($) => $.adaptivePlanner.itemPanel.integration.syncErrorToast)),
      },
    );
  };

  const integrationsAvailable = eligibleIntegrationsCount > 0;
  const buttonDisabled = isAssigned ? isSynced || isSyncing : !integrationsAvailable;
  const buttonName =
    associatedIntegrationName ?? t(($) => $.adaptivePlanner.itemPanel.integration.addButton);

  if (isAssociatedLoading || isSyncStatusLoading) {
    return <Skeleton data-testid="item-panel-integration-field-loading" className="h-10 w-full" />;
  }

  return (
    <>
      <div
        data-testid={isAssigned ? syncStateTestId : undefined}
        className="flex items-center gap-2 text-sm"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="min-w-0 flex-1">
              <SelectIntegrationButton
                disabled={buttonDisabled}
                name={buttonName}
                isAssigned={isAssigned}
                onClick={() => setDialogOpen(true)}
              />
            </span>
          </TooltipTrigger>
          {!isAssigned && !integrationsAvailable && (
            <TooltipContent>
              {t(($) => $.adaptivePlanner.itemPanel.integration.addButtonDisabledTooltip)}
            </TooltipContent>
          )}
        </Tooltip>
        {isAssigned && !isSynced && !isFailed && (
          <Button
            variant="outline"
            className="h-10 shrink-0"
            disabled={isSyncing}
            onClick={handleSyncClick}
          >
            <Sync
              className={
                isSyncing ? 'h-4 w-4 animate-spin [animation-direction:reverse]' : 'h-4 w-4'
              }
            />
            {t(($) => $.adaptivePlanner.itemPanel.integration.syncButton)}
          </Button>
        )}
        {isAssigned && isFailed && (
          <Tooltip>
            <TooltipTrigger asChild>
              {canViewSyncFailures ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onViewSyncFailures}
                  className="h-auto shrink-0 cursor-pointer p-0 hover:bg-transparent"
                >
                  <FailedStatusBadge />
                </Button>
              ) : (
                // biome-ignore lint/a11y/noNoninteractiveTabindex: the badge takes focus so the sync error tooltip is reachable without a pointer
                <span className="shrink-0" tabIndex={0}>
                  <FailedStatusBadge />
                </span>
              )}
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex max-w-xs flex-col gap-1">
                {syncErrorMessage && <span>{syncErrorMessage}</span>}
                {canViewSyncFailures && (
                  <span>
                    {t(($) => $.common.settings['work-integrations'].toasts.sync.viewFailures)}
                  </span>
                )}
                {!canViewSyncFailures && !syncErrorMessage && (
                  <span>
                    {t(($) => $.adaptivePlanner.itemPanel.integration.failedTooltipFallback)}
                  </span>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <AssignIntegrationDialog
        key={associatedIntegrationId ?? 'unassigned'}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        itemId={itemId}
        itemTypeId={itemTypeId}
        integrations={integrations ?? []}
        currentIntegrationId={associatedIntegrationId}
      />
    </>
  );
};
